import React from 'react';
import { PublicWidgetReview, WidgetStyleConfig } from '../../types';

interface WidgetPreviewProps {
  config: WidgetStyleConfig;
  reviews: PublicWidgetReview[];
  averageRating: number;
  total: number;
  companyName?: string;
}

function Stars({ rating, color }: { rating: number; color: string }) {
  const outOfFive = Math.max(0, Math.min(5, Number(rating) / 2));
  return (
    <span className="widget-preview-stars" aria-hidden="true">
      {Array.from({ length: 5 }, (_, i) => (
        <svg key={i} viewBox="0 0 24 24" width="16" height="16">
          <path
            d="M12 2.5l2.9 6.1 6.7.9-4.8 4.7 1.2 6.6L12 17.8 6 20.8l1.2-6.6L2.4 9.5l6.7-.9L12 2.5z"
            fill={outOfFive >= i + 0.75 ? color : 'none'}
            stroke={color}
            strokeWidth="1.4"
            strokeLinejoin="round"
          />
        </svg>
      ))}
    </span>
  );
}

function formatDate(iso: string) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const WidgetPreview: React.FC<WidgetPreviewProps> = ({
  config,
  reviews,
  averageRating,
  total,
  companyName,
}) => {
  const visible = reviews.slice(0, config.max_reviews || 10);
  const layout = config.layout || 'carousel';

  const rootStyle: React.CSSProperties = {
    fontFamily: config.font_family,
    fontSize: `${config.font_size}px`,
    color: config.text_color,
    background: config.background_color,
    border: `1px solid ${config.border_color}`,
    borderRadius: `${config.border_radius}px`,
    padding: 20,
    lineHeight: 1.5,
  };

  const cardStyle: React.CSSProperties = {
    background: config.card_background_color,
    border: `1px solid ${config.border_color}`,
    borderRadius: Math.max(0, config.border_radius - 4),
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minWidth: 0,
  };

  const bodyClass =
    layout === 'list' ? 'widget-preview-list' : layout === 'grid' ? 'widget-preview-grid' : 'widget-preview-carousel';

  return (
    <div className="widget-preview-root" style={rootStyle}>
      <div className="widget-preview-header">
        {config.title ? (
          <h3 style={{ margin: 0, fontSize: '1.35em', color: config.name_color }}>{config.title}</h3>
        ) : null}
        {config.show_average && total > 0 ? (
          <div className="widget-preview-avg" style={{ color: config.name_color }}>
            <Stars rating={averageRating} color={config.star_color} />
            <strong>{Number(averageRating || 0).toFixed(1)}</strong>
            <span style={{ fontWeight: 400, color: config.text_color }}>
              ({total} review{total === 1 ? '' : 's'})
            </span>
          </div>
        ) : null}
      </div>

      {visible.length === 0 ? (
        <div className="widget-preview-empty">
          No reviews with “Show on Site” enabled{companyName ? ` for ${companyName}` : ''} yet.
        </div>
      ) : (
        <div className={bodyClass}>
          {visible.map((review) => (
            <article key={review.id} style={cardStyle}>
              <div className="widget-preview-card-top">
                <div className="widget-preview-avatar" style={{ background: config.accent_color }}>
                  {review.initial || 'C'}
                </div>
                <div>
                  {config.show_name ? (
                    <div style={{ fontWeight: 700, color: config.name_color }}>{review.name}</div>
                  ) : null}
                  {config.show_date && review.date ? (
                    <div style={{ fontSize: '0.8em', opacity: 0.85 }}>{formatDate(review.date)}</div>
                  ) : null}
                </div>
              </div>
              {config.show_rating ? (
                <div className="widget-preview-rating-row">
                  <Stars rating={review.rating} color={config.star_color} />
                  <span style={{ fontSize: '0.85em', fontWeight: 600, color: config.name_color }}>
                    {Number(review.rating).toFixed(1)} / 10
                  </span>
                </div>
              ) : null}
              {config.show_comment && review.comment ? (
                <p className="widget-preview-comment">{review.comment}</p>
              ) : null}
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default WidgetPreview;
